import type { PushNotificationsPlugin, Token } from '@capacitor/push-notifications';
import { getDeviceToken } from './better-auth';
import { isNativePlatform, getPlatform } from './native-features';

let PushNotifications: PushNotificationsPlugin | null = null;
let pluginLoadAttempted = false;

async function loadPlugin(): Promise<PushNotificationsPlugin | null> {
  if (!isNativePlatform()) return null;
  if (pluginLoadAttempted) return PushNotifications;
  pluginLoadAttempted = true;
  try {
    const mod = await import('@capacitor/push-notifications');
    PushNotifications = mod.PushNotifications;
    // Check if the plugin actually has native implementation
    if (typeof PushNotifications?.register !== 'function') {
      console.warn('[NativePush] Plugin loaded but no native implementation');
      PushNotifications = null;
    }
    return PushNotifications;
  } catch {
    console.warn('[NativePush] Plugin not available');
    return null;
  }
}

export async function initNativePushNotifications(userId: string): Promise<void> {
  try {
    const plugin = await loadPlugin();
    if (!plugin) return;

    // Request permission
    let permResult;
    try {
      permResult = await plugin.requestPermissions();
    } catch {
      console.warn('[NativePush] Permission request failed');
      return;
    }

    if (permResult.receive !== 'granted') {
      console.log('[NativePush] Permission not granted:', permResult.receive);
      return;
    }

    // Register for push
    await plugin.register();

    // Listen for registration token
    plugin.addListener('registration', async (token: Token) => {
      console.log('[NativePush] FCM token received');
      await registerFcmToken(userId, token.value);
    });

    plugin.addListener('registrationError', (error) => {
      console.error('[NativePush] Registration error:', error);
    });

    // Handle incoming notifications
    plugin.addListener('pushNotificationReceived', (notification) => {
      console.log('[NativePush] Notification received:', notification.title);
    });

    // Handle notification tap
    plugin.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[NativePush] Notification tapped:', action.notification.data);
      const data = action.notification.data as Record<string, string>;
      if (data?.link) {
        window.location.href = data.link;
      }
    });

    console.log('[NativePush] Initialized');
  } catch (error) {
    // PushNotifications native methods may not be implemented on all platforms
    console.warn('[NativePush] Initialization skipped:', error);
  }
}

async function registerFcmToken(userId: string, token: string): Promise<void> {
  try {
    const platform = getPlatform() as 'ios' | 'android';
    const API_BASE = import.meta.env.DEV ? import.meta.env.VITE_API_URL : '/api';
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const deviceToken = getDeviceToken();
    if (deviceToken) {
      headers['Authorization'] = `Bearer ${deviceToken}`;
    }

    await fetch(`${API_BASE}/users/${userId}/fcm-token`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        token,
        platform,
        appVersion: '1.48.0',
      }),
    });
    console.log('[NativePush] FCM token registered');
  } catch (error) {
    console.error('[NativePush] Failed to register FCM token:', error);
  }
}
